import type {Route} from '@/types/route';
import {RouteComparator, type RouteComparison} from './RouteComparator';
import {RouteTrafficAnalyzer} from './RouteTrafficAnalyzer';

export interface RouteDisplayData {
  route: Route;
  index: number;
  advantage: string;
  trafficColor: string;
  isSelected: boolean;
}

export interface RouteSelectionContext {
  primaryRoute: Route;
  alternativeRoutes: Route[];
  selectedRouteIndex?: number;
}

/**
 * Enhanced service for route comparison and display logic
 * Builds on RouteComparator to provide UI-specific functionality
 */
export class RouteComparisonService {
    /**
   * Get route advantage text for display in UI
   * @param {Route} primaryRoute - The primary route to compare against
   * @param {Route} alternativeRoute - The alternative route to compare
   * @return {string} Advantage text for the alternative route
   */
    static getRouteAdvantage(primaryRoute: Route, alternativeRoute: Route): string {
        const comparison = RouteComparator.compareRoute(primaryRoute, alternativeRoute);
        return comparison.advantage;
    }

    /**
   * Get traffic color for a route using RouteTrafficAnalyzer
   * @param {Route} route - The route to analyze for traffic color
   * @return {string} Traffic color for the route
   */
    static getRouteTrafficColor(route: Route): string {
        return RouteTrafficAnalyzer.getRouteTrafficColor(route);
    }

    /**
   * Prepare alternative routes for display with all necessary data
   * @param {Route} primaryRoute - The primary route to compare against
   * @param {Route[]} alternativeRoutes - Array of alternative routes
   * @param {number} selectedRouteIndex - Index of the currently selected route
   * @return {RouteDisplayData[]} Array of route display data
   */
    static prepareAlternativesForDisplay(
        primaryRoute: Route,
        alternativeRoutes: Route[],
        selectedRouteIndex?: number,
    ): RouteDisplayData[] {
        return alternativeRoutes.map((route, index) => ({
            route,
            index: index + 2, // Alternative routes start from "Route 2"
            advantage: this.getRouteAdvantage(primaryRoute, route),
            trafficColor: this.getRouteTrafficColor(route),
            isSelected: selectedRouteIndex === index + 1, // +1 because primary is index 0
        }));
    }

    /**
   * Get detailed comparison between two routes
   * @param {Route} primaryRoute - The primary route to compare
   * @param {Route} alternativeRoute - The alternative route to compare
   * @return {RouteComparison} Detailed comparison object
   */
    static getDetailedComparison(primaryRoute: Route, alternativeRoute: Route): RouteComparison {
        return RouteComparator.compareRoute(primaryRoute, alternativeRoute);
    }

    /**
   * Find the best alternative route based on criteria
   * @param {Route} primaryRoute - The primary route
   * @param {Route[]} alternativeRoutes - Array of alternative routes
   * @param {'distance' | 'time'} criteria - Criteria for determining best route
   * @return {Route | null} Best route or null if no alternatives
   */
    static findBestAlternative(
        primaryRoute: Route,
        alternativeRoutes: Route[],
        criteria: 'distance' | 'time' = 'time',
    ): Route | null {
        if (alternativeRoutes.length === 0) return null;

        return RouteComparator.findBestRoute([primaryRoute, ...alternativeRoutes], criteria);
    }

    /**
   * Check if an alternative route has a significant advantage
   * @param {Route} primaryRoute - The primary route to compare against
   * @param {Route} alternativeRoute - The alternative route to check
   * @return {boolean} True if alternative has significant advantage
   */
    static hasSignificantAdvantage(primaryRoute: Route, alternativeRoute: Route): boolean {
        const comparison = this.getDetailedComparison(primaryRoute, alternativeRoute);
        return comparison.advantage !== '';
    }

    /**
   * Get summary statistics for all routes
   * @param {Route} primaryRoute - The primary route
   * @param {Route[]} alternativeRoutes - Array of alternative routes
   * @return {Object} Summary statistics for all routes
   */
    static getRoutesSummary(primaryRoute: Route, alternativeRoutes: Route[]) {
        const allRoutes = [primaryRoute, ...alternativeRoutes];
        const stats = RouteComparator.getRouteComparisonStats(allRoutes);

        return {
            totalRoutes: allRoutes.length,
            hasAlternatives: alternativeRoutes.length > 0,
            stats,
            bestByTime: RouteComparator.findBestRoute(allRoutes, 'time'),
            bestByDistance: RouteComparator.findBestRoute(allRoutes, 'distance'),
        };
    }

    /**
   * Format route comparison for display
   * @param {RouteComparison} comparison - The route comparison object
   * @return {string} Formatted comparison text
   */
    static formatRouteComparison(comparison: RouteComparison): string {
        const {distanceDifference, durationDifference} = comparison;

        if (Math.abs(distanceDifference) > 5000) { // 5km threshold
            const kmDiff = Math.abs(distanceDifference / 1000);
            return distanceDifference > 0 ?
                `${kmDiff.toFixed(1)}km shorter` :
                `${kmDiff.toFixed(1)}km longer`;
        }

        if (Math.abs(durationDifference) > 300) { // 5 minutes threshold
            const minDiff = Math.abs(durationDifference / 60);
            return durationDifference > 0 ?
                `${minDiff.toFixed(0)} min faster` :
                `${minDiff.toFixed(0)} min slower`;
        }

        return 'Similar route';
    }

    /**
   * Validate route selection context
   * @param {RouteSelectionContext} context - The route selection context to validate
   * @return {boolean} True if context is valid
   */
    static validateSelectionContext(context: RouteSelectionContext): boolean {
        if (!context.primaryRoute) {
            return false;
        }

        if (context.alternativeRoutes.some((route) => !route || !route.distanceMeters)) {
            return false;
        }

        return true;
    }

    /**
   * Log route comparison for debugging
   * @param {Route} primaryRoute - The primary route
   * @param {Route} alternativeRoute - The alternative route to compare
   * @param {number} index - Index of the alternative route
   */
    static logRouteComparison(primaryRoute: Route, alternativeRoute: Route, index: number): void {
        const comparison = this.getDetailedComparison(primaryRoute, alternativeRoute);
    }
}
